import React, { useState } from 'react';
import { Upload, Button, message, Row, Col, Typography } from 'antd';
import { UploadOutlined, FileExcelOutlined, SaveOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';
import { invoiceService } from '../../services';
import { getApiErrorMessage } from '../../utils/error';
import './ImportPrescriptionPage.css';

const { Text, Link } = Typography;

const ImportInvoiceExcelPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [downloading, setDownloading] = useState(false);

  const importMutation = useMutation({
    mutationFn: (file: File) => invoiceService.importExcel(file),
    onSuccess: (data) => {
      const imported = data.data?.imported;
      if (typeof imported === 'number') {
        message.success(`Import thành công ${imported} hóa đơn!`);
      } else {
        message.success('Import thành công!');
      }
      queryClient.invalidateQueries({ queryKey: ['invoices-excel'] });
      setFileList([]);
      navigate('/facility/invoices/from-excel');
    },
    onError: (error: unknown) => {
      message.error(getApiErrorMessage(error, 'Import thất bại!'));
    },
  });

  const handleUploadChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
    setFileList(newFileList.slice(-1));
  };

  const handleImport = () => {
    if (fileList.length === 0) {
      message.warning('Vui lòng chọn file Excel để import!');
      return;
    }

    const file = fileList[0].originFileObj;
    if (file) {
      importMutation.mutate(file);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      setDownloading(true);
      const blob = await invoiceService.downloadSampleInvoiceExcel();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'mau_nhap_hoa_don.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success('Tải file mẫu thành công!');
    } catch (error: unknown) {
      message.error(getApiErrorMessage(error, 'Không thể tải file mẫu!'));
    } finally {
      setDownloading(false);
    }
  };

  const uploadProps: UploadProps = {
    beforeUpload: () => false,
    onChange: handleUploadChange,
    fileList,
    accept: '.xlsx,.xls',
    maxCount: 1,
  };

  return (
    <div className="import-prescription-page">
      <h1 className="page-title">Nhập hóa đơn excel</h1>

      <div className="import-section">
        <Row gutter={48} align="middle">
          <Col>
            <div className="upload-section">
              <Text className="section-label">Tải file exel lên</Text>
              <Upload {...uploadProps}>
                <Button icon={<UploadOutlined />}>Tải file exel lên</Button>
              </Upload>
            </div>
          </Col>
          <Col>
            <div className="template-section">
              <Text className="section-label">File excel mẫu</Text>
              <div className="template-link">
                <FileExcelOutlined className="excel-icon" />
                <Link onClick={handleDownloadTemplate} disabled={downloading}>
                  {downloading ? 'Đang tải...' : 'Tải file excel mẫu nhập hóa đơn'}
                </Link>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      <div className="form-actions">
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleImport}
          loading={importMutation.isPending}
          size="large"
        >
          Lưu hóa đơn
        </Button>
      </div>
    </div>
  );
};

export default ImportInvoiceExcelPage;
